import React from 'react';
import { useSelector } from 'react-redux';
import { Sparkles, CheckCircle2 } from 'lucide-react';
import { useT } from '../utils/translate';
import { curLabel } from '../utils/currency';

function fmt(n) {
  return Number(n || 0).toLocaleString('en-US');
}

function BalanceCard({ balances, t }) {
  if (!balances?.length) {
    return (
      <div className="card-sm text-center text-sm text-ink-tertiary">{t('home.noBalance')}</div>
    );
  }
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(balances.length, 2)}, minmax(0,1fr))` }}>
      {balances.map(b => {
        const weOwe = b.amount > 0;
        const amount = Math.abs(b.amount);
        return (
          <div key={b.currency} className="card-sm">
            <p className="text-xs font-medium text-ink-tertiary uppercase tracking-wide">{curLabel(b.currency)}</p>
            <p className={`text-xl font-bold mt-1 ${amount === 0 ? 'text-ink' : weOwe ? 'text-primary-600' : 'text-red-600'}`}>
              {fmt(amount)}
            </p>
            {amount > 0 && (
              <p className="text-xs text-ink-tertiary mt-0.5">{weOwe ? t('home.weOwe') : t('home.theyOwe')}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CashbackCard({ balances, t }) {
  const nonZero = (balances || []).filter(b => b.amount > 0.0001);
  if (!nonZero.length) return null;
  return (
    <div>
      <p className="text-sm font-semibold text-ink mb-2">{t('home.cashback')}</p>
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(nonZero.length, 2)}, minmax(0,1fr))` }}>
        {nonZero.map(b => (
          <div key={b.currency} className="card-sm">
            <p className="text-xs font-medium text-ink-tertiary uppercase tracking-wide">{curLabel(b.currency)}</p>
            <p className="text-xl font-bold mt-1 text-emerald-600">{fmt(b.amount)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Tier oralig'ini o'qiladigan qatorga aylantiradi: "0 – 1 000 000", "5 000 000+".
function tierRangeLabel(tier) {
  const from = fmt(tier.minAmount);
  if (!tier.maxAmount) return `${from}+`;
  return `${from} – ${fmt(tier.maxAmount)}`;
}

// So'nggi xarid summasi shu bosqichga tushadimi — inklyuziv chegaralar,
// backend (computeCashbackAccrual)dagi bilan bir xil qoida.
function tierMatchesAmount(tier, amount) {
  return amount >= tier.minAmount && (!tier.maxAmount || amount <= tier.maxAmount);
}

function CashbackProgramCard({ organization, lastPurchase, t }) {
  if (!organization?.cashbackEnabled) return null;
  const tiers = organization.cashbackTiers || [];
  if (!tiers.length) return null;
  // Valyuta bo'yicha guruhlash, har birida bosqichlar minAmount bo'yicha o'sish tartibida.
  const byCurrency = {};
  tiers.forEach(tr => { (byCurrency[tr.currency] ||= []).push(tr); });
  Object.values(byCurrency).forEach(list => list.sort((a, b) => a.minAmount - b.minAmount));

  return (
    <div className="card-body card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4 text-emerald-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-ink">{t('home.cashbackProgramTitle')}</p>
          <p className="text-xs text-ink-tertiary">{t('home.cashbackProgramHint')}</p>
        </div>
      </div>
      <div className="space-y-3">
        {Object.entries(byCurrency).map(([currency, list]) => (
          <div key={currency}>
            {Object.keys(byCurrency).length > 1 && (
              <p className="text-xs font-medium text-ink-tertiary uppercase tracking-wide mb-1.5">{curLabel(currency)}</p>
            )}
            <div className="rounded-xl border border-surface-200 divide-y divide-surface-100 overflow-hidden">
              {list.map((tr, i) => {
                const isTop = i === list.length - 1;
                const isCurrent = lastPurchase?.currency === currency && tierMatchesAmount(tr, lastPurchase.amount);
                return (
                  <div key={i} className={`flex items-center justify-between px-3 py-2 ${isCurrent ? 'bg-primary-50' : isTop ? 'bg-emerald-50/60' : ''}`}>
                    <span className="text-sm text-ink-secondary flex items-center gap-1.5">
                      {tierRangeLabel(tr)} {curLabel(currency)}
                      {isCurrent && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-primary-600 text-white text-[10px] font-semibold">
                          <CheckCircle2 className="w-3 h-3" /> {t('home.cashbackCurrentTier')}
                        </span>
                      )}
                    </span>
                    <span className={`text-sm font-bold ${isCurrent ? 'text-primary-700' : isTop ? 'text-emerald-700' : 'text-ink'}`}>{tr.percent}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HomePage() {
  const t = useT();
  const customer = useSelector((s) => s.auth.customer);
  return (
    <div className="p-4 space-y-4">
      <div className="card-body card">
        <p className="text-sm text-ink-tertiary">{t('home.welcome')}</p>
        <p className="text-lg font-bold text-ink mt-0.5">{customer?.name}</p>
        <p className="text-sm text-ink-secondary mt-0.5">{customer?.phone}</p>
      </div>
      <div>
        <p className="text-sm font-semibold text-ink mb-2">{t('home.balance')}</p>
        <BalanceCard balances={customer?.balances} t={t} />
      </div>
      <CashbackCard balances={customer?.cashbackBalances} t={t} />
      <CashbackProgramCard organization={customer?.organization} lastPurchase={customer?.lastPurchase} t={t} />
    </div>
  );
}
